import type { RecipeListQuery } from '@recipes/contracts';
import { Button, Skeleton } from '@recipes/ui';
import { ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { RecipeCard } from '@/components/recipes/recipe-card';
import { useRecipeList, useToggleFavorite } from './queries';

export interface RecipeRowProps {
  title: string;
  params: Partial<RecipeListQuery>;
  /** Link to the library with these filters applied. */
  seeAllTo?: string;
  emptyText: string;
  emptyAction?: ReactNode;
  limit?: number;
  testId?: string;
}

/** Dashboard section: a horizontally scrollable strip of recipe cards. */
export function RecipeRow({
  title,
  params,
  seeAllTo,
  emptyText,
  emptyAction,
  limit = 6,
  testId,
}: RecipeRowProps) {
  const { t } = useTranslation();
  const query = useRecipeList({ ...params, limit });
  const toggleFavorite = useToggleFavorite();
  const items = query.data?.pages[0]?.items ?? [];
  const offlineCopy = query.data?.pages[0]?.offlineCopy ?? false;

  return (
    <section aria-labelledby={`${testId ?? title}-heading`} data-testid={testId}>
      <div className="mb-2 flex items-center justify-between">
        <h2 id={`${testId ?? title}-heading`} className="text-lg font-semibold">
          {title}
        </h2>
        {seeAllTo && items.length > 0 && (
          <Button asChild variant="ghost" size="sm">
            <Link to={seeAllTo}>
              {t('dashboard.seeAll')} <ChevronRight aria-hidden />
            </Link>
          </Button>
        )}
      </div>
      {query.isPending ? (
        <div className="flex gap-3 overflow-hidden">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-56 w-56 shrink-0" />
          ))}
        </div>
      ) : query.isError ? (
        <p className="text-sm text-muted-foreground">{t('dashboard.loadError')}</p>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          <p>{emptyText}</p>
          {emptyAction && <div className="mt-2">{emptyAction}</div>}
        </div>
      ) : (
        <ul className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
          {items.map((recipe) => (
            <li key={recipe.id} className="w-56 shrink-0 snap-start">
              <RecipeCard
                recipe={recipe}
                offlineCopy={offlineCopy}
                onToggleFavorite={(r, next) => toggleFavorite.mutate({ id: r.id, favorite: next })}
                className="h-full"
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
